// A small menu behind a button: opens on click, closes on Escape, a click
// elsewhere or a choice; arrow keys move between items.
import { type CSSProperties, type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface Props {
  /** Draws the button; spread the props onto it. */
  trigger: (props: { 'aria-expanded': boolean; 'aria-haspopup': 'menu'; 'aria-controls': string; onClick: () => void }) => ReactNode;
  children: (close: () => void) => ReactNode;
  placement?: 'down' | 'up';
  /** Draw the list on the page, so a scrolling sidebar can't clip it. */
  fixed?: boolean;
}

function fixedStyle(trigger: HTMLElement, placement: 'down' | 'up'): CSSProperties {
  const rect = trigger.getBoundingClientRect();
  if (placement === 'down') return { position: 'fixed', top: rect.bottom + 6, right: Math.max(8, window.innerWidth - rect.right), zIndex: 70 };
  // A narrow rail opens beside the icon; a wide sidebar opens above the row.
  if (rect.width < 80) return { position: 'fixed', left: rect.right + 8, bottom: Math.max(8, window.innerHeight - rect.bottom), zIndex: 70 };
  return { position: 'fixed', left: rect.left, bottom: Math.max(8, window.innerHeight - rect.top + 8), zIndex: 70 };
}

export function Menu({ trigger, children, placement = 'down', fixed = false }: Props) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const box = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const close = () => setOpen(false);
  const triggerEl = open && fixed ? box.current?.querySelector<HTMLElement>('[aria-haspopup]') : null;
  const style = triggerEl ? fixedStyle(triggerEl, placement) : undefined;

  useEffect(() => {
    if (!open) return;
    const items = () => [...(list.current?.querySelectorAll<HTMLElement>('[role="menuitem"], [role="menuitemradio"]') ?? [])];
    items()[0]?.focus();
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!box.current?.contains(target) && !list.current?.contains(target)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      const entries = items();
      const at = entries.indexOf(document.activeElement as HTMLElement);
      if (e.key === 'Escape') {
        setOpen(false);
        box.current?.querySelector<HTMLElement>('[aria-haspopup]')?.focus();
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const next = (at + (e.key === 'ArrowDown' ? 1 : -1) + entries.length) % entries.length;
        entries[next]?.focus();
      }
    };
    // A list drawn on the page would stay put while its row scrolls away.
    const onScroll = (e: Event) => {
      if (fixed && !list.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [open, fixed]);

  const menu = open && (
    <div id={id} ref={list} role="menu" className={`menu__list menu__list--${placement}`} style={fixed ? style : undefined}>
      {children(close)}
    </div>
  );
  return (
    <div className="menu" ref={box}>
      {trigger({ 'aria-expanded': open, 'aria-haspopup': 'menu', 'aria-controls': id, onClick: () => setOpen((o) => !o) })}
      {fixed ? menu && createPortal(menu, document.body) : menu}
    </div>
  );
}
